// Phase 7 D3 — Track C T-C3. ThemeSync bridge.
//
// `<ThemeProvider/>` (and the `theme-init.js` cookie hint) handles the
// pre-paint flicker. After AuthProvider resolves, this component reads
// `GET /api/users/me/preferences` and reconciles ThemeProvider's local
// state with the server's stored preference. This is a deliberate
// component (rendered inside the authed shell) rather than a hook on
// ThemeProvider itself so the ThemeProvider stays useful in test
// harnesses that do not want a network round-trip.
//
// Behaviour:
//   - mount inside <RequireAuth> (so /api/users/me/preferences is callable).
//   - on first successful fetch, if the server's preference differs from
//     the in-memory value, push the server's preference through
//     setPreference (which also rewrites the cookie hint).
//   - re-mounts on signOut/signIn cycles automatically because the
//     queryClient resets on signOut.
//
// Renders nothing.

import * as React from 'react';
import { usePreferences } from '../hooks/usePreferences.js';
import { useTheme } from './ThemeContext.js';

export function ThemeSync(): null {
  const { setPreference, preference } = useTheme();
  const { data, isSuccess } = usePreferences();
  const lastSyncedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!isSuccess || !data) return;
    if (lastSyncedRef.current === data.theme) return;
    lastSyncedRef.current = data.theme;
    if (data.theme !== preference) {
      setPreference(data.theme);
    }
    // We deliberately do not list `preference` in deps — the server's value
    // wins on first sync; subsequent local edits are reflected via
    // setPreference and are not overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  return null;
}
